/* eslint-disable @typescript-eslint/no-explicit-any */

import { IBrowserAccessor } from './browser';
import { ILogger } from './logger';

/**
 * Generic data persistence manager that handles consistent storage logic
 * across both localStorage and cookieStorage.
 *
 * This class is designed to store, retrieve, and clear JSON-serializable data
 * in two places (localStorage and cookies) to support platform requirements
 * such as subdomain access and redundancy.
 *
 * Key Features:
 * - Generic: handles any JSON-serializable data type via generics.
 * - Dual Persistence: writes to both localStorage and cookies.
 * - Freshest Data Strategy: when both sources are valid, the item with the most recent `timestamp` field is returned.
 */

export interface IDataStorageManager {
    saveData<T>(key: string, value: T): Promise<void>;
    loadData<T>(key: string): Promise<T | null>;
    clearData(key: string): Promise<void>;
}

export class DataStorageManager implements IDataStorageManager {

    private readonly browser: IBrowserAccessor;
    private readonly logger?: ILogger;

    constructor(browser: IBrowserAccessor, logger?: ILogger) {
        this.browser = browser;
        this.logger = logger;
    }

    async saveData<T>(key: string, value: T): Promise<void> {
        try {
            const stringified = JSON.stringify(value);
            await Promise.all([
                this.browser.localStorage.setItem(key, stringified),
                this.browser.cookieStorage.setItem(key, stringified)
            ]);
        } catch (error) {
            this.logger?.writeErrorLog({
                method: 'DataStorageManager::saveData()',
                message: error.message,
                errorMessage: error.stack || error.message
            });
            throw new Error(`Failed to save data: ${error.message}`);
        }
    }

    async loadData<T>(key: string): Promise<T | null> {
        let cookieData: T | null = null;
        let localData: T | null = null;

        try {
            const cookieRaw = await this.browser.cookieStorage.getItem(key);
            if (cookieRaw) {
                const decoded = decodeURIComponent(cookieRaw);
                cookieData = JSON.parse(decoded) as T;
            }
         } catch (error) {
            this.logger?.writeWarningLog({
                method: 'DataStorageManager::loadData()',
                message: 'Failed to parse cookie data.',
                errorMessage: error.message,
            });
        }

        try {
            const localRaw = await this.browser.localStorage.getItem(key);
            if (localRaw) localData = JSON.parse(localRaw) as T;
        } catch (error) {
            this.logger?.writeWarningLog({
                method: 'DataStorageManager::loadData()',
                message: 'Failed to parse localStorage data.',
                errorMessage: error.message,
            });
        }

        return this.getFreshestValidSnapshot(cookieData, localData);
    }

    async clearData(key: string): Promise<void> {
        try {
            await Promise.all([
                this.browser.localStorage.removeItem(key),
                this.browser.cookieStorage.removeItem(key)
            ]);
        } catch (error) {
            this.logger?.writeErrorLog({
                method: 'DataStorageManager::clearData()',
                message: 'Failed to clear data.',
                errorMessage: error.message
            });
            throw new Error(`Failed to clear data: ${error.message}`);
        }
    }

    private getFreshestValidSnapshot<T>(cookie: T | null, local: T | null): T | null {
        if (cookie && local) {
            const cookieTs = (cookie as any)?.dataUpdatedAt;
            const localTs = (local as any)?.dataUpdatedAt;
            if (cookieTs && localTs) return cookieTs > localTs ? cookie : local;
        }
        return cookie ?? local;
    }
}
